"use client";

import { useState } from "react";
import {
  CipherGrid,
  CipherWorkbench,
  type GridTone,
} from "@/components/tool-kit/cipher-workbench";
import { DataTable, Notice } from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  ALPHABET,
  applyTextOptions,
  foreignLetters,
  KEEP_ALL_TEXT,
  lettersOnly,
} from "@/lib/tool-kit/ciphers/alphabet";
import { atbash, atbashSteps } from "@/lib/tool-kit/ciphers/atbash";

const STEP_LIMIT = 12;
const HALF = 13;
const PLAIN_ROWS = [ALPHABET.slice(0, HALF), ALPHABET.slice(HALF)];

/** English strings. A translated content file overrides these through `ui`. */
export const ATBASH_CIPHER_STRINGS = {
  inputLabel: "Text to encode or decode",
  inputPlaceholder: "Type or paste text — the same action encodes and decodes",
  sample: "Hello World",
  outputLabel: "Atbash result",
  keep: "Keep",
  keepCase: "Upper and lower case",
  keepSpaces: "Spaces and line breaks",
  keepPunctuation: "Punctuation and numbers",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  foreign:
    "one: {count} letter is outside A–Z and was left as it is: {letters}|other: {count} letters are outside A–Z and were left as they are: {letters}",
  working: "Alphabet table and working",
  rule: "Each letter at position n in the alphabet (A = 1, Z = 26) becomes the letter at position 27 − n. Encoding and decoding are the same step, so running the result through again gives back your text.",
  tableLabel: "Atbash alphabet",
  plainRow: "Plain",
  cipherRow: "Atbash",
  tableHint:
    "Letters in your text are highlighted in the plain rows; the letters they become are highlighted below them.",
  stepsHeading:
    "one: Letter by letter — the first {count} letter|other: Letter by letter — the first {count} letters",
  letter: "Letter",
  position: "Position",
  mirrored: "27 − position",
  result: "Result",
  noLetters: "Type some letters to see the working.",
} as const;

function alphabetCells(): string[] {
  return PLAIN_ROWS.flatMap((plain) => [
    ...plain,
    ...[...plain].map((letter) => atbash(letter)),
  ]);
}

const CELLS = alphabetCells();

export default function AtbashCipherWidget() {
  const strings = useToolUi(ATBASH_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<string>(strings.sample);
  const [options, setOptions] = useState(KEEP_ALL_TEXT);

  const output = applyTextOptions(atbash(input), options);
  const foreign = foreignLetters(input);
  const used = new Set(lettersOnly(input));
  const steps = atbashSteps(input, STEP_LIMIT);

  // Rows alternate plain / Atbash, 13 letters each.
  const tone = (index: number): GridTone | undefined => {
    const letter = CELLS[index];
    const isPlainRow = Math.floor(index / HALF) % 2 === 0;
    if (isPlainRow) {
      return used.has(letter) ? "primary" : undefined;
    }
    return used.has(atbash(letter)) ? "secondary" : undefined;
  };

  return (
    <CipherWorkbench
      idPrefix="atb"
      input={input}
      labels={{
        input: strings.inputLabel,
        inputPlaceholder: strings.inputPlaceholder,
        output: strings.outputLabel,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        working: strings.working,
      }}
      notices={
        foreign.count > 0 ? (
          <Notice>
            {tPlural(strings.foreign, locale, foreign.count, {
              count: fmt(foreign.count),
              letters: foreign.letters.join(" "),
            })}
          </Notice>
        ) : null
      }
      onInputChange={setInput}
      output={output}
      textOptions={{
        value: options,
        onChange: setOptions,
        show: ["keepCase", "keepSpaces", "keepPunctuation"],
      }}
      working={
        <>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.rule}
          </p>
          <div className="space-y-2">
            <CipherGrid
              cells={CELLS}
              columns={HALF}
              dense
              label={strings.tableLabel}
              rowHeaders={[
                strings.plainRow,
                strings.cipherRow,
                strings.plainRow,
                strings.cipherRow,
              ]}
              tone={tone}
            />
            <p className={HINT}>{strings.tableHint}</p>
          </div>
          {steps.length > 0 ? (
            <div className="space-y-2">
              <p className={LABEL}>
                {tPlural(strings.stepsHeading, locale, steps.length, {
                  count: fmt(steps.length),
                })}
              </p>
              <DataTable
                head={[
                  strings.letter,
                  strings.position,
                  strings.mirrored,
                  strings.result,
                ]}
                rows={steps.map((step) => [
                  step.letter,
                  fmt(step.position),
                  fmt(step.mirrored),
                  step.result,
                ])}
              />
            </div>
          ) : (
            <Notice>{strings.noLetters}</Notice>
          )}
        </>
      }
    />
  );
}
